import { useState, useEffect, useRef } from 'react'
import ListenButton from '@/components/atoms/ListenButton/ListenButton'
import RecordingBox from '@/components/atoms/RecordingBox/RecordingBox'
import * as styles from '@/components/organisms/Learning/LearningSytle.css'
import clsx from 'clsx'
import { getStudentActivityTitle } from '@/utils/activityTitle';

interface DictationProps {
  activity?: any;
  unitData?: any;
  onResponse?: (response: any) => void;
  existingResponse?: any;
  isHomework?: boolean;
}

export default function StudentDictation({ activity, unitData, onResponse, existingResponse, isHomework = false }: DictationProps) {
  const [userInput, setUserInput] = useState(existingResponse?.userInput || '');
  // 기존 응답에 녹음이 있으면 블러 활성화
  const [isBlurred, setIsBlurred] = useState(!!existingResponse?.recordingUrl || !!existingResponse?.recordingBlob);
  const inputRef = useRef<HTMLInputElement>(null);
  const audioRef = useRef<HTMLAudioElement>(null);

  // activity.items에서 플래시카드 데이터 가져오기
  const activityData = activity?.items?.[0] || {};
  let content: any = {};
  
  try {
    content = activityData.contentJson ? JSON.parse(activityData.contentJson) : {};
  } catch (e) {
    console.error('contentJson 파싱 에러:', e);
  }

  // 플래시카드 데이터 구조에 맞춰 파싱
  const word = content.word || content.sentence || content.text || 'winter';
  const translation = content.translation || content.korean || content.meaning || '겨울';
  const audioUrl = content.audioUrl || content.audio || null;
  const title = getStudentActivityTitle(activity?.title, '딕테이션');

  console.log('=== Dictation 데이터 (플래시카드) ===');
  console.log('Activity:', activity);
  console.log('Content:', content);
  console.log('Word:', word);
  console.log('Translation:', translation);
  console.log('Existing Response:', existingResponse);
  console.log('Is Blurred:', isBlurred);

  const [wrongCount, setWrongCount] = useState(existingResponse?.wrongCount || 0);
  const [isInputDisabled, setIsInputDisabled] = useState(false);
  const [showAnswer, setShowAnswer] = useState(wrongCount >= 5);

  const getByteLength = (value: string) => {
    let len = 0;
    for (let i = 0; i < value.length; i += 1) {
      len += value.charCodeAt(i) > 127 ? 2 : 1;
    }
    return len;
  };

  const meetsHomeworkReady = (text: string, recordedFlag?: boolean) => {
    const recordedSatisfied = typeof recordedFlag === 'boolean' ? recordedFlag : isBlurred;
    return recordedSatisfied && getByteLength(text.trim()) >= 1;
  };

  // 마운트 시 초기 응답 저장
  useEffect(() => {
    if (onResponse && !existingResponse) {
      onResponse({
        userInput: '',
        isCorrect: false,
        wrongCount: 0,
        isHomeworkReady: false,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 정답 체크 (word 기준)
  const isCorrect = userInput === word;
  const hasInput = userInput.trim().length > 0;
  
  // userInput 변경 시 응답 저장
  const handleInputChange = (value: string) => {
    setUserInput(value);
    if (onResponse) {
      onResponse({
        userInput: value,
        isCorrect: value === word,
        wrongCount,
        isHomeworkReady: meetsHomeworkReady(value),
      });
    }
  };

  const handleWrongAnswer = () => {
    if (isHomework) return;
    const nextWrong = wrongCount + 1;
    setWrongCount(nextWrong);

    if (nextWrong >= 5) {
      setShowAnswer(true);
    }

    if (audioRef.current && audioUrl) {
      setIsInputDisabled(true);
      try {
        audioRef.current.currentTime = 0;
        audioRef.current.play().catch((error) => {
          console.error('오디오 자동 재생 실패:', error);
          setIsInputDisabled(false);
        });
      } catch (error) {
        console.error('오디오 재생 중 오류:', error);
        setIsInputDisabled(false);
      }

      setTimeout(() => {
        setIsInputDisabled(false);
      }, 10000);
    }

    if (onResponse) {
      onResponse({
        userInput,
        isCorrect: false,
        wrongCount: nextWrong,
        isHomeworkReady: meetsHomeworkReady(userInput),
      });
    }
  };

  const handleInputBlur = () => {
    if (isHomework || wrongCount >= 5) return;
    if (!hasInput) return;

    if (!isCorrect) {
      handleWrongAnswer();
    }
  };

  // 녹음 완료 시 블러 활성화
  const handleRecordingComplete = (audioBlob: Blob, audioUrl: string) => {
    console.log('녹음 완료, 블러 활성화');
    setIsBlurred(true);
    
    // 응답에 녹음 정보 저장
    if (onResponse) {
      onResponse({
        userInput,
        isCorrect: isCorrect,
        recordingBlob: audioBlob,
        recordingUrl: audioUrl,
        wrongCount,
        isHomeworkReady: meetsHomeworkReady(userInput, true),
      });
    }
  };

  // 입력칸이 나타나면 자동 포커스
  useEffect(() => {
    if (isBlurred && inputRef.current) {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
  }, [isBlurred]);

  return (
    <main>
     <div>
        <h1 className='px-5 pt-5 pb-3 heading1'>{title}</h1>
        <div className='p-5 pt-0'>
          <div className={styles.gameCard}>
            <div className='px-14 py-4'>
                <div className='relative'>
                    <p 
                      className={clsx(
                        '!text-4xl font-bold pb-2 text-center break-words',
                        { 'blur-sm select-none': isBlurred }
                      )}
                    >
                      {word}
                    </p>
                    {/* 블러 처리되지 않는 해석 */}
                </div>
                <p className='mt-2 text-center text-gray-500'>{translation}</p>
            </div>
            <ListenButton size={48} className='absolute top-4 right-4' audioSrc={audioUrl} />
          </div>
          <RecordingBox 
            onRecordingComplete={handleRecordingComplete}
            existingAudioUrl={existingResponse?.recordingUrl}
          />
          {!isHomework && wrongCount > 0 && wrongCount < 5 && (
            <p className='mt-3 text-sm text-center text-gray-500'>
              <span className='text-red-500'>오답 {wrongCount}회</span> - 다시 듣고 따라 적어보세요.
            </p>
          )}
          {!isHomework && showAnswer && (
            <p className='mt-3 text-sm font-bold text-center text-orange-600'>
              정답: {word} — 보여진 문장을 따라 써보세요.
            </p>
          )}
          {/* 녹음 완료 후에만 입력칸 표시 */}
          {isBlurred && (
            <input
              ref={inputRef}
              placeholder="띄어쓰기 포함해서 입력해보세요"
              type="text"
              value={userInput}
              onChange={(e) => handleInputChange(e.target.value)}
              onBlur={handleInputBlur}
              disabled={isInputDisabled}
              className={clsx(
                styles.graInput, 
                'mt-[30px]',
                {
                  '!border-green-500 !text-green-600 !border-3': !isHomework && hasInput && isCorrect,
                  '!border-red-500 !text-red-600 !border-3': !isHomework && hasInput && !isCorrect,
                  'opacity-50 cursor-not-allowed': isInputDisabled,
                  'bg-yellow-50 border-yellow-400 placeholder-yellow-700': showAnswer && !hasInput,
                }
              )}
            />
          )}
          {!isHomework && audioUrl && (
            <audio
              ref={audioRef}
              src={audioUrl}
              style={{ display: 'none' }}
              onEnded={() => {
                setIsInputDisabled(false);
                if (inputRef.current) {
                  requestAnimationFrame(() => inputRef.current?.focus());
                }
              }}
            />
          )}
        </div>
     </div>
    </main>
  )
}